<style lang="scss" src="./contact-us-draver.scss"></style>

<template>
  <a-drawer
    :open="open"
    class="consultation-drawer"
    root-class-name="consultation-drawer"
    @close="onClose"
  >
    <template #title>
      <div class="consultation-drawer__header-shell">
        <div class="consultation-drawer__header-overline">Консультация</div>
        <div class="consultation-drawer__header-lede">
          <h1>Поможем, если у вас остались вопросы по оформлению заявок.</h1>
        </div>
      </div>
    </template>

    <template #closeIcon>
      <span class="consultation-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <a-form ref="drawerFormRef" :model="form" :rules="rules" layout="vertical">
      <div class="consultation-drawer__form-container">
        <a-form-item
          class="form-full-name-field stc-ant-field-skin"
          label="Компания"
          name="companyName"
          required
        >
          <a-input
            class="form-full-name-field__input"
            v-model:value="form.companyName"
            placeholder="Название компании"
          />
        </a-form-item>
        <FormFullNameField v-model:model-value="form.fullName" />
        <div class="consultation-drawer__row">
            <FormPhoneField v-model:model-value="form.phone" :required="false" />
            <FormEmailField v-model:model-value="form.email" />
        </div>
        <FormSubjectField v-model:model-value="form.subject" :options="subjectOptions" />
        <FormQuestionField v-model:model-value="form.question" />
      </div>
    </a-form>

    <div class="consultation-drawer__contact-cards">
      <div class="consultation-drawer__contact-cards-item">
        <div class="consultation-drawer__contact-cards-item-icon">
          <img :src="`${ICONS_BASE}/email.svg`" alt="" />
        </div>
        <div class="consultation-drawer__contact-cards-item-body">
          <span class="consultation-drawer__contact-cards-item-label">Через почту</span>
          <a class="consultation-drawer__contact-cards-item-link" href="mailto:info@sticmsu.ru"
            >info@sticmsu.ru</a
          >
        </div>
      </div>
      <div class="consultation-drawer__contact-cards-item">
        <div class="consultation-drawer__contact-cards-item-icon">
          <img :src="`${ICONS_BASE}/phone-2.svg`" alt="" />
        </div>
        <div class="consultation-drawer__contact-cards-item-body">
          <span class="consultation-drawer__contact-cards-item-label">По телефону</span>
          <a class="consultation-drawer__contact-cards-item-link" href="tel:8-495-939-20-15"
            >8-495-939-20-15</a
          >
        </div>
      </div>
    </div>

    <a-divider class="consultation-drawer__mobile-divider" />

    <FormPrivacyConsentField
      v-model:model-value="form.privacy"
      class="consultation-drawer__mobile-policy"
    />

    <template #footer>
      <div class="consultation-drawer__toolbar">
        <FormPrivacyConsentField v-model:model-value="form.privacy" class="consultation-drawer__footer-policy" />
        <div class="consultation-drawer__buttons-container">
          <button type="button" class="btn btn-secondary btn-medium btn-cancel" @click="onClose">
            Отмена
          </button>
          <button type="button" class="btn btn-primary btn-medium" @click="onSubmit" :disabled="isSubmitDisabled">
            {{ isSubmitting ? 'Отправка…' : 'Отправить' }}
          </button>
        </div>
      </div>
    </template>
  </a-drawer>

  <ConsultationRequestSuccessModal
    v-model:open="openModal"
    :description="successModalDescription"
  />
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';

import FormFullNameField from '@/js/components/form/form-full-name-field.vue';
import FormPhoneField from '@/js/components/form/form-phone-field.vue';
import FormEmailField from '@/js/components/form/form-email-field.vue';
import FormSubjectField, {
  type FormSubjectOption,
} from '@/js/components/form/form-subject-field.vue';
import FormQuestionField from '@/js/components/form/form-question-field.vue';
import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { getRecaptchaErrorMessage } from '@/js/utils/getRecaptchaToken';
import FormPrivacyConsentField from '@/js/components/form/form-privacy-consent-field.vue';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

interface ConsultationFormMetaItem {
  id: string;
  text: string;
}

interface ConsultationFormMetaResponse {
  success?: boolean;
  data?: {
    subject?: ConsultationFormMetaItem[];
    privacy?: ConsultationFormMetaItem[];
  };
  msg?: string;
}

const subjectOptions = ref<FormSubjectOption[]>([]);
const privacyOptionId = ref<string>('');

const openModal = ref<boolean>(false);
const successModalDescription = ref('');
const isSubmitting = ref(false);

const showModal = () => {
  openModal.value = true;
};

const drawerFormRef = ref<FormInstance>();

const initialFormState = () => ({
  fullName: '',
  companyName: '',
  email: '',
  phone: '',
  subject: '',
  question: '',
  privacy: false,
});

const form = reactive(initialFormState());

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.fullName) &&
    isNotBlankString(form.companyName) &&
    isNotBlankString(form.email) &&
    isNotBlankString(form.subject) &&
    isNotBlankString(form.question) &&
    form.privacy,
);

const hasValidationErrors = computed(() => !isValidEmail(form.email));
const isSubmitDisabled = computed(
  () => isSubmitting.value || !isRequiredFieldsFilled.value || hasValidationErrors.value,
);

const rules: Record<string, Rule[]> = {
  fullName: [{ required: true, message: 'Введите ваше полное имя' }],
  companyName: [{ required: true, message: 'Введите название компании' }],
  email: [{ required: true, message: 'Введите e-mail', type: 'email' }],
  subject: [{ required: true, message: 'Выберите тему обращения' }],
  question: [{ required: true, message: 'Опишите ваш вопрос' }],
};

const open = ref<boolean>(false);

const openDrawer = (): void => {
  open.value = true;
};

const fetchConsultationFormMeta = async () => {
  try {
    const response = await fetch(getApiUrl('/api/addConsultForm.php'), {
      method: 'GET',
      credentials: 'same-origin',
    });
    const result = (await response.json()) as ConsultationFormMetaResponse;

    if (!response.ok || result.success === false) {
      throw new Error(result.msg ?? 'Не удалось загрузить данные формы');
    }

    const subjectList = result.data?.subject ?? [];
    const privacyList = result.data?.privacy ?? [];

    subjectOptions.value = subjectList.map((item) => ({
      value: item.id,
      label: item.text,
    }));
    privacyOptionId.value = privacyList[0]?.id ?? '';
  } catch {
    message.error('Не удалось загрузить темы обращения');
  }
};

const resetFormState = () => {
  Object.assign(form, initialFormState());
  nextTick(() => {
    drawerFormRef.value?.clearValidate();
  });
};

const onClose = () => {
  open.value = false;
  resetFormState();
};

const onSubmit = async () => {
  if (!drawerFormRef.value) {
    return;
  }

  try {
    await drawerFormRef.value.validate();
  } catch {
    return;
  }

  if (form.privacy && !privacyOptionId.value) {
    message.error('Не удалось определить согласие на обработку данных');
    return;
  }

  isSubmitting.value = true;

  try {
    const submitResult = await submitFormWithCaptcha({
      endpoint: getApiUrl('/api/addConsultForm.php'),
      buildFormData: () => {
        const body = new FormData();
        body.append('email', form.email);
        body.append('phone', form.phone);
        body.append('fio', form.fullName);
        body.append('company_name', form.companyName);
        body.append('subject', form.subject);
        body.append('question', form.question);
        if (form.privacy && privacyOptionId.value) {
          body.append('privacy', privacyOptionId.value);
        }

        return body;
      },
    });

    if (!submitResult.ok || !submitResult.data) {
      if (submitResult.errorCode === 'captcha-not-loaded') {
        message.error(getRecaptchaErrorMessage('captcha-not-loaded'));
        return;
      }

      if (submitResult.errorCode === 'captcha-failed') {
        message.error(getRecaptchaErrorMessage('captcha-failed'));
        return;
      }

      message.error(submitResult.message ?? 'Не удалось отправить обращение');
      return;
    }

    successModalDescription.value = submitResult.data.msg ?? '';
    trackYaGoal('submitApplication');
    onClose();
    showModal();
  } catch {
    message.error('Ошибка соединения');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  void fetchConsultationFormMeta();
});

defineExpose({
  open: openDrawer,
});
</script>
